import { useAuthStore } from '../stores/auth';

/**
 * Check if user has a specific permission
 * @param permission - Permission string (e.g., 'classes:view', 'classes:create')
 * @returns boolean
 */
export function hasPermission(permission: string): boolean {
  const authStore = useAuthStore();
  const user = authStore.user;

  if (!user) {
    return false;
  }

  // School admin users have access to everything
  if (user.userType === 'admin') {
    return true;
  }

  // If user has 'manage' permission for the module, grant all permissions
  const [module] = permission.split(':');
  if (user.permissions?.includes(`${module}:manage`)) {
    return true;
  }

  // Check specific permission
  return user.permissions?.includes(permission) ?? false;
}

/**
 * Check if user has any of the specified permissions
 * @param permissions - Array of permission strings
 * @returns boolean
 */
export function hasAnyPermission(permissions: string[]): boolean {
  return permissions.some(permission => hasPermission(permission));
}

/**
 * Check if user has all of the specified permissions
 * @param permissions - Array of permission strings
 * @returns boolean
 */
export function hasAllPermissions(permissions: string[]): boolean {
  return permissions.every(permission => hasPermission(permission));
}

/**
 * Class-related permissions
 */
export const CLASS_PERMISSIONS = {
  VIEW: 'classes:view',
  CREATE: 'classes:create',
  UPDATE: 'classes:update',
  DELETE: 'classes:delete',
  ARCHIVE: 'classes:archive',
  MANAGE: 'classes:manage',
} as const;

/**
 * Staff-related permissions
 */
export const STAFF_PERMISSIONS = {
  VIEW: 'sp:staff:view',
  VIEW_FINANCIALS: 'sp:staff:financials:view',
  EDIT_FINANCIALS: 'sp:staff:financials:update',
} as const;
